import React from "react";
import { Habit, Day, ReportPeriod } from "../types";
import { getPeriodDays, generatePeriodDays } from "../utils/periodUtils";
import { HabitBarChart } from "./HabitBarChart";

interface ProgressTrackingWidgetProps {
  habits: Habit[];
  days: Day[];
  period?: ReportPeriod;
}

export const ProgressTrackingWidget: React.FC<ProgressTrackingWidgetProps> = ({
  habits,
  days,
  period = "month",
}) => {
  const trackedHabits = habits.filter(
    (habit) =>
      !habit.archived,
  );

  if (trackedHabits.length === 0) {
    return null;
  }

  const numDays = getPeriodDays(period);
  const periodDays = generatePeriodDays(period);
  const useTableView = numDays <= 10;

  return (
    <div
      style={{
        marginTop: "20px",
      }}
    >
      {useTableView ? (
        // Table view for ≤10 days
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {trackedHabits.map((habit) => (
            <div
              key={habit.id}
              style={{
                padding: "4px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    flexGrow: 1,
                    fontSize: "0.8em",
                  }}
                >
                  {habit.name}
                </span>
                <small
                  style={{
                    color: "#777",
                    fontSize: "0.8em",
                  }}
                >
                  {habit.progressTracking ? `(${habit.progressUnit})` : ""}
                </small>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.min(numDays, 10)}, 1fr)`,
                  gap: "4px",
                  marginBottom: "4px",
                }}
              >
                {periodDays.map((day) => (
                  <div
                    key={`day-${day}`}
                    style={{
                      textAlign: "center",
                      fontSize: "0.7em",
                      color: "#777",
                      fontWeight: "bold",
                    }}
                  >
                    {new Date(day).getDate()}
                  </div>
                ))}

                {periodDays.map((day) => {
                  const dayData = days.find((d) => d.date === day);
                  const hasCompleted = dayData?.habits.includes(habit.id);
                  const progress = hasCompleted
                    ? dayData?.progress?.[habit.id] || 0
                    : undefined;

                  return (
                    <div
                      key={`progress-${day}`}
                      style={{
                        textAlign: "center",
                        backgroundColor: hasCompleted
                          ? habit.progressTracking
                            ? `rgba(${habit.score >= 0 ? "40, 167, 69" : "220, 53, 69"}, 0.15)`
                            : `rgba(40, 167, 69, 0.15)`
                          : "transparent",
                        padding: "6px 2px",
                        borderRadius: "4px",
                        fontSize: "0.9em",
                        fontWeight: hasCompleted ? "bold" : "normal",
                        color: hasCompleted ? "#000" : "#999",
                      }}
                    >
                      {habit.progressTracking
                        ? progress !== undefined
                          ? progress
                          : "-"
                        : hasCompleted
                          ? "✓"
                          : "-"}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Bar chart view for >10 days
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {trackedHabits.map((habit) => (
            <div
              key={habit.id}
              style={{
                padding: "4px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    flexGrow: 1,
                    fontSize: "0.8em",
                  }}
                >
                  {habit.name}
                </span>
                <small
                  style={{
                    color: "#777",
                    fontSize: "0.8em",
                  }}
                >
                  {habit.progressTracking ? `(${habit.progressUnit})` : ""}
                </small>
              </div>

              <HabitBarChart
                habit={habit}
                days={days}
                periodDays={periodDays}
                height={50}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
